import React, { Suspense, lazy, startTransition, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UsageProvider } from './contexts/UsageContext';
import { AffiliateProvider } from './contexts/AffiliateContext';
import { GenerationProvider } from './contexts/GenerationContext';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import Layout from './layout/Layout';
import { blogPosts } from './components/Blog/blogData';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';
import { getDoc, doc } from 'firebase/firestore';
import { db } from './firebase/config';
import UpgradeModal from './components/UpgradeModal/UpgradeModal';
import AffiliatePolicy from './pages/AffiliatePolicy';

// Lazy load components with preload
const LandingPage = lazy(() => import(/* webpackPrefetch: true */ './components/LandingPage'));
const SpotifyRemixer = lazy(() => import(/* webpackPrefetch: true */ './components/SpotifyRemixer/Mixify'));
const About = lazy(() => import('./components/About'));
const AppInterface = lazy(() => import(/* webpackPrefetch: true */ './components/AppInterface'));
const Documentation = lazy(() => import('./pages/Documentation/Documentation'));
const ScrollToTop = lazy(() => import('./components/ScrollToTop'));
const PrivacyPolicy = lazy(() => import('./pages/Legal/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/Legal/TermsOfService'));
const CookiePolicy = lazy(() => import('./pages/Legal/CookiePolicy'));
const RefundPolicy = lazy(() => import('./pages/Legal/RefundPolicy'));
const Settings = lazy(() => import('./pages/Settings/Settings'));
const Support = lazy(() => import('./pages/Support/Support'));
const NightcoreConverter = lazy(() => import('./components/NightcoreConverter/NightcoreConverter'));
const PhonkRemixer = lazy(() => import('./components/PhonkRemixer/PhonkRemixer').then(module => ({ default: module.PhonkRemixer })));
const SpeedUpRemixer = lazy(() => import('./components/SpeedUpRemixer/SpeedUpRemixer'));
const Tutorials = lazy(() => import('./pages/Tutorials/Tutorials'));
const Blog = lazy(() => import('./components/Blog/Blog'));
const BlogPost = lazy(() => import('./components/Blog/BlogPost'));
const PricingPage = lazy(() => import('./components/PricingPage/PricingPage'));
const AffiliateAuth = lazy(() => import('./components/Affiliate/AffiliateAuth'));
const AffiliateDashboard = lazy(() => import('./components/Affiliate/AffiliateDashboard'));
const AffiliateAdmin = lazy(() => import('./components/Affiliate/AffiliateAdmin'));
const Donate = lazy(() => import('./pages/Donate/Donate'));
const NightcorePage = lazy(() => import('./components/NightcorePage'));

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { currentUser } = useAuth();
  const location = useLocation();
  
  if (!currentUser) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }
  
  return children;
};

// Protected Route component with admin check
const AdminRoute = ({ children }) => {
  const { currentUser } = useAuth();
  const ADMIN_EMAILS = ['mail.esstafasoufiane@gmail.com'];

  if (!currentUser || !ADMIN_EMAILS.includes(currentUser.email)) {
    return <Navigate to="/" />;
  }

  return children;
};

// Protected Route component for affiliates
const AffiliateRoute = ({ children }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const checkAffiliateStatus = async () => {
      if (!currentUser) {
        navigate('/affiliate/login');
        return;
      }

      try {
        const waitlistDoc = await getDoc(doc(db, 'affiliate-waitlist', currentUser.uid));
        const affiliateDoc = await getDoc(doc(db, 'affiliates', currentUser.uid));

        if (!waitlistDoc.exists() || !affiliateDoc.exists() || waitlistDoc.data().status !== 'approved') {
          navigate('/affiliate/login');
        }
      } catch (error) {
        console.error('Error checking affiliate status:', error);
        navigate('/affiliate/login');
      }
    };

    checkAffiliateStatus();
  }, [currentUser, navigate]);

  if (!currentUser) {
    return null;
  }

  return children;
};

// Loading fallback component
const LoadingFallback = () => (
  <div style={{ 
    height: '100vh', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    background: '#000',
    color: 'white'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div className="loading-spinner"></div>
      <p>Loading...</p>
    </div>
  </div>
);

// Route wrapper component
const RouteWrapper = ({ element: Element, ...props }) => {
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        <Element {...props} />
      </Suspense>
    </ErrorBoundary>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <Helmet>
          <title>Reverbeat - Professional Audio Effects & Music Production Tools</title>
          <meta name="description" content="Transform your music with Reverbeat's professional audio effects. Create stunning reverb, delay, and other effects for your tracks. The ultimate music production tool." />
          <meta name="keywords" content="reverbeat, audio effects, music production, reverb, delay, music tools, audio processing" />
          <meta property="og:title" content="Reverbeat - Professional Audio Effects & Music Production Tools" />
          <meta property="og:description" content="Transform your music with Reverbeat's professional audio effects. Create stunning reverb, delay, and other effects for your tracks." />
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="Reverbeat" />
          <link rel="canonical" href="https://reverbeat.com" />
          <script type="application/ld+json">
            {`
              {
                "@context": "https://schema.org",
                "@type": "WebApplication",
                "name": "Reverbeat",
                "description": "Professional audio effects and music production tools",
                "applicationCategory": "MultimediaApplication",
                "operatingSystem": "Web Browser",
                "offers": {
                  "@type": "Offer",
                  "price": "0",
                  "priceCurrency": "USD"
                }
              }
            `}
          </script>
        </Helmet>
        <AuthProvider>
          <UsageProvider>
            <AffiliateProvider>
              <GenerationProvider>
                <Router>
                  <ScrollToTop />
                  <Layout>
                    <ErrorBoundary>
                      <Suspense fallback={<LoadingFallback />}>
                        <Routes>
                          <Route path="/" element={<RouteWrapper element={LandingPage} />} />
                          <Route path="/about" element={<RouteWrapper element={About} />} />
                          <Route path="/documentation" element={<RouteWrapper element={Documentation} />} />
                          <Route path="/privacy" element={<RouteWrapper element={PrivacyPolicy} />} />
                          <Route path="/terms" element={<RouteWrapper element={TermsOfService} />} />
                          <Route path="/cookies" element={<RouteWrapper element={CookiePolicy} />} />
                          <Route path="/refund" element={<RouteWrapper element={RefundPolicy} />} />
                          <Route path="/support" element={<RouteWrapper element={Support} />} />
                          <Route path="/tutorials" element={<RouteWrapper element={Tutorials} />} />
                          <Route path="/blog" element={<RouteWrapper element={Blog} />} />
                          <Route 
                            path="/blog/:slug" 
                            element={
                              <RouteWrapper 
                                element={(props) => <BlogPost {...props} posts={blogPosts} />} 
                              />
                            } 
                          />
                          <Route path="/nightcore" element={<RouteWrapper element={NightcoreConverter} />} />
                          <Route path="/nightcore-gif" element={<RouteWrapper element={NightcorePage} />} />
                          <Route 
                            path="/phonk" 
                            element={
                              <RouteWrapper 
                                element={props => (
                                  <ProtectedRoute>
                                    <PhonkRemixer {...props} />
                                  </ProtectedRoute>
                                )}
                              />
                            } 
                          />
                          <Route path="/spotify" element={<RouteWrapper element={SpotifyRemixer} />} />
                          <Route path="/speedup" element={<RouteWrapper element={SpeedUpRemixer} />} />
                          <Route path="/settings" element={<RouteWrapper element={Settings} />} />
                          <Route path="/pricing" element={<RouteWrapper element={PricingPage} />} />
                          <Route path="/app" element={<RouteWrapper element={AppInterface} />} />
                          <Route path="/donate" element={<RouteWrapper element={Donate} />} />
                          
                          {/* Affiliate Routes */}
                          <Route path="/affiliate/login" element={<RouteWrapper element={AffiliateAuth} />} />
                          <Route 
                            path="/affiliate/admin" 
                            element={
                              <AdminRoute>
                                <RouteWrapper element={AffiliateAdmin} />
                              </AdminRoute>
                            } 
                          />
                          <Route 
                            path="/affiliate/dashboard" 
                            element={
                              <AffiliateRoute>
                                <RouteWrapper element={AffiliateDashboard} />
                              </AffiliateRoute>
                            }
                          />
                          <Route path="/affiliate-policy" element={<RouteWrapper element={AffiliatePolicy} />} />
                        </Routes>
                      </Suspense>
                    </ErrorBoundary>
                  </Layout>
                  <UpgradeModal />
                </Router>
              </GenerationProvider>
            </AffiliateProvider>
          </UsageProvider>
        </AuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
